import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Menu, X, Phone, Mail, MapPin, ChevronRight } from 'lucide-react';

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({ name: '', phone: '', message: '' });
  };

  return (
    <div className="bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 w-full bg-white shadow-md z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h2 className="text-2xl font-black text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                Shizah Mobility
              </h2>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#about" className="text-gray-700 hover:text-shizah-blue transition-colors">
                About
              </a>
              <a href="#partners" className="text-gray-700 hover:text-shizah-blue transition-colors">
                Partners
              </a>
              <a href="#founder" className="text-gray-700 hover:text-shizah-blue transition-colors">
                Founder
              </a>
              <a href="#contact" className="text-gray-700 hover:text-shizah-blue transition-colors">
                Contact
              </a>
              <Link
                to="/login"
                className="bg-shizah-blue text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-all transform hover:-translate-y-0.5"
              >
                Employee Login
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-700 hover:text-shizah-blue"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <a
                href="#about"
                className="block text-gray-700 hover:text-shizah-blue"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </a>
              <a
                href="#partners"
                className="block text-gray-700 hover:text-shizah-blue"
                onClick={() => setMobileMenuOpen(false)}
              >
                Partners
              </a>
              <a
                href="#founder"
                className="block text-gray-700 hover:text-shizah-blue"
                onClick={() => setMobileMenuOpen(false)}
              >
                Founder
              </a>
              <a
                href="#contact"
                className="block text-gray-700 hover:text-shizah-blue"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact
              </a>
              <Link
                to="/login"
                className="block bg-shizah-blue text-white px-6 py-2 rounded-md text-center"
              >
                Employee Login
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h1 className="text-5xl md:text-6xl font-black mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Shizah Mobility
              </h1>
              <p className="text-2xl text-shizah-blue font-semibold mb-4">
                Your Comfort, Our Priority
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Providing reliable earning opportunities for drivers with fixed salary and growth support.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#contact"
                  className="bg-shizah-green text-white px-8 py-3 rounded-md font-semibold hover:bg-green-600 transition-all transform hover:-translate-y-1 shadow-lg text-center"
                >
                  Join as Driver
                </a>
                <Link
                  to="/login"
                  className="bg-shizah-blue text-white px-8 py-3 rounded-md font-semibold hover:bg-blue-700 transition-all transform hover:-translate-y-1 shadow-lg text-center"
                >
                  Employee Login
                </Link>
              </div>
            </div>
            <div className="flex justify-center items-center">
              <img
                src="https://customer-assets.emergentagent.com/job_9308f2ee-4a90-4036-bc1e-09ce5941433b/artifacts/cosm4gyf_Shizah.jpeg"
                alt="Shizah Mobility"
                className="w-full max-w-md rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Us */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black font-heading text-gray-900 mb-4">About Us</h2>
            <div className="w-20 h-1 bg-shizah-blue mx-auto mb-8"></div>
          </div>
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg text-gray-700 leading-relaxed">
              Shizah Mobility is a Mumbai-based mobility company focused on empowering drivers by providing
              vehicles, stable income, and a professional working environment. We believe in creating
              sustainable livelihood opportunities while maintaining the highest standards of service and
              professionalism.
            </p>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section id="partners" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black font-heading text-gray-900 mb-4">Our Partners</h2>
            <div className="w-20 h-1 bg-shizah-blue mx-auto mb-8"></div>
            <p className="text-gray-600">Trusted collaborations driving our success</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <img
                src="https://customer-assets.emergentagent.com/job_shizah-fleet/artifacts/a82fiixk_everest.png"
                alt="Everest Fleet"
                className="w-full h-24 object-contain"
              />
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <img
                src="https://customer-assets.emergentagent.com/job_shizah-fleet/artifacts/qi3pv9ee_li.jpg"
                alt="Lithium Urban Technologies"
                className="w-full h-24 object-contain"
              />
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <img
                src="https://customer-assets.emergentagent.com/job_shizah-fleet/artifacts/5pzpxnt5_refex.jpg"
                alt="Refex"
                className="w-full h-24 object-contain"
              />
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow flex items-center justify-center">
              <span className="text-2xl font-bold text-gray-700">Wiki Lab U&V</span>
            </div>
          </div>
        </div>
      </section>

      {/* Founder's Desk */}
      <section id="founder" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black font-heading text-gray-900 mb-4">Founder's Desk</h2>
            <div className="w-20 h-1 bg-shizah-blue mx-auto mb-8"></div>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-1 flex flex-col items-center">
                <div className="relative w-56 h-64 overflow-hidden rounded-full mb-4">
                  <img
                    src="https://customer-assets.emergentagent.com/job_shizah-fleet/artifacts/u9rlnnrq_shahid.jpeg"
                    alt="Mr. Ansari Shahid Ameen"
                    className="w-full h-full object-cover object-top shadow-xl"
                    style={{ objectPosition: 'center 20%' }}
                  />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mt-2 text-center">
                  Mr. Ansari Shahid Ameen
                </h3>
                <p className="text-shizah-blue text-center font-semibold text-sm">Founder & CEO</p>
              </div>
              <div className="md:col-span-2 bg-blue-50 p-6 rounded-lg">
                <p className="text-base text-gray-700 leading-relaxed italic">
                  "Our mission is to create stable and respectful earning opportunities for drivers while
                  building a trusted mobility ecosystem. At Shizah Mobility, we don't just provide vehicles;
                  we provide dignity, security, and a path to financial stability. Every driver is a valued
                  partner in our journey toward transforming urban transportation."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black font-heading text-gray-900 mb-4">
              What Our Drivers Say
            </h2>
            <div className="w-20 h-1 bg-shizah-blue mx-auto mb-8"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <div className="text-yellow-500 mb-4 text-2xl">★★★★★</div>
              <p className="text-gray-700 mb-4">
                "Good salary and support from the company. The management is very understanding and helpful."
              </p>
              <p className="font-semibold text-gray-900">- Rajesh Kumar</p>
              <p className="text-sm text-gray-500">Driver since 2023</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <div className="text-yellow-500 mb-4 text-2xl">★★★★★</div>
              <p className="text-gray-700 mb-4">
                "Weekly off and fixed income is very helpful for my family. I feel secure working here."
              </p>
              <p className="font-semibold text-gray-900">- Mohammed Aslam</p>
              <p className="text-sm text-gray-500">Driver since 2022</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <div className="text-yellow-500 mb-4 text-2xl">★★★★★</div>
              <p className="text-gray-700 mb-4">
                "Professional environment and trusted management. Best decision I made for my career."
              </p>
              <p className="font-semibold text-gray-900">- Suresh Patil</p>
              <p className="text-sm text-gray-500">Driver since 2024</p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black font-heading text-gray-900 mb-4">Our Team</h2>
            <div className="w-20 h-1 bg-shizah-blue mx-auto mb-8"></div>
            <p className="text-gray-600">Dedicated professionals supporting our drivers</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-32 h-32 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-4xl text-gray-400">👨‍💼</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Operations Manager</h3>
              <p className="text-gray-600">Ensuring smooth daily operations</p>
            </div>
            <div className="text-center">
              <div className="w-32 h-32 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-4xl text-gray-400">👥</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Driver Support Team</h3>
              <p className="text-gray-600">24/7 assistance for our drivers</p>
            </div>
            <div className="text-center">
              <div className="w-32 h-32 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-4xl text-gray-400">🚗</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Field Executive</h3>
              <p className="text-gray-600">On-ground support and coordination</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Us */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black font-heading text-gray-900 mb-4">Contact Us</h2>
            <div className="w-20 h-1 bg-shizah-blue mx-auto mb-8"></div>
            <p className="text-gray-600">Get in touch with us today</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-shizah-blue p-3 rounded-full">
                    <Phone className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Phone</h3>
                    <p className="text-gray-600">+91 7977 679 205</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-shizah-blue p-3 rounded-full">
                    <Mail className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Email</h3>
                    <p className="text-gray-600">shizahmobility@gmail.com</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-shizah-blue p-3 rounded-full">
                    <MapPin className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Location</h3>
                    <p className="text-gray-600">Mumbai, Maharashtra, India</p>
                  </div>
                </div>
                <a
                  href="https://wa.me/917977679205"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-green-500 text-white px-6 py-3 rounded-md hover:bg-green-600 transition-colors"
                >
                  <svg
                    className="w-6 h-6 mr-2"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  WhatsApp Us
                </a>
              </div>
            </div>
            <div>
              <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-md">
                <div className="mb-4">
                  <label className="block text-gray-700 font-semibold mb-2">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-shizah-blue"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 font-semibold mb-2">Phone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    pattern="[0-9]{10}"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-shizah-blue"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 font-semibold mb-2">Message *</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-shizah-blue"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-shizah-blue text-white py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_9308f2ee-4a90-4036-bc1e-09ce5941433b/artifacts/cosm4gyf_Shizah.jpeg"
                alt="Shizah Mobility"
                className="h-16 mb-4 bg-white p-2 rounded"
              />
              <p className="text-gray-400">Your Comfort, Our Priority</p>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <a href="#about" className="text-gray-400 hover:text-white transition-colors">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="#partners" className="text-gray-400 hover:text-white transition-colors">
                    Partners
                  </a>
                </li>
                <li>
                  <a href="#contact" className="text-gray-400 hover:text-white transition-colors">
                    Contact
                  </a>
                </li>
                <li>
                  <Link to="/login" className="text-gray-400 hover:text-white transition-colors">
                    Employee Login
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Follow Us</h3>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-400">
            <p>&copy; 2024 Shizah Mobility. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
